import { type ReactElement, useCallback, useRef } from 'react';
import cn from 'classnames';
import { HStack, Link, List, ListItem, ListSection, Popup, type TestProps, Text } from '@direct-frontend/components';
import { useToggle } from '@direct-frontend/stdlib';
import { YCP_CABINET_NAVIGATION } from '#shared/lib/const';
import { trackMerchantsGoal } from '#shared/lib/trackGoal';
import { StepStatus } from '../StepStatus';
import styles from './Step.module.css';

export type StepProps = {
    name: string;
    status?: 'pending' | 'done';
    href?: string;
    description?: string;
    children?: ReactElement<typeof ListItem>[];
    isActive?: boolean;
    refetch: VoidFunction;
} & TestProps;

export const Step = ({
    name,
    status,
    children,
    href,
    description,
    isActive,
    refetch,
    'data-testid': dataTestId = 'Step',
}: StepProps) => {
    const { value: isDescriptionOpen, toggleOn: openDescription, toggleOff: closeDescription } = useToggle(false);

    const ref = useRef<HTMLDivElement>(null);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    const handleClick = useCallback(() => {
        refetch();
        trackMerchantsGoal(YCP_CABINET_NAVIGATION, { menuType: 'HEADER_ONBOARDING_STEPS', buttonType: href });
    }, [href, refetch]);

    const handleMouseLeave = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
            closeDescription();
        }, 100);
    }, [closeDescription]);

    const handleMouseEnter = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        openDescription();
    }, [openDescription]);

    if (status === undefined) {
        return null;
    }

    return (
        <>
            <div
                data-testid={dataTestId}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                className={cn(styles.step, isActive && styles.active)}
                ref={ref}
                data-status={status}
            >
                <Link data-testid="Link" href={href} onClick={handleClick} className={styles.stepBox}>
                    <HStack gap="8" alignItems="center">
                        <StepStatus data-testid={`${dataTestId}.Status`} status={status} />
                        <Text data-testid={`${dataTestId}.Name`} className={styles.name} size="xs" role="main">
                            {name}
                        </Text>
                    </HStack>
                </Link>
            </div>
            <Popup
                flip="never"
                placement="bottom-start"
                shouldResize={false}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                isOpen={isDescriptionOpen}
                strategy="fixed"
                triggerRef={ref}
            >
                <List width={256} className={styles.stepList} size="m">
                    {description ? (
                        <ListSection>
                            <ListItem
                                // DIRECT-337096: элемент исключён из поиска, при доработке заменить на текст для поиска
                                textValue={null}
                            >
                                <Text size="s" role="supplementary">
                                    {description}
                                </Text>
                            </ListItem>
                        </ListSection>
                    ) : null}

                    <ListSection>{children}</ListSection>
                </List>
            </Popup>
        </>
    );
};
